import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { formatPrice } from '../lib/utils';
import { 
  Package, 
  Calendar, 
  Eye, 
  Truck, 
  CheckCircle, 
  Clock, 
  XCircle,
  Loader2,
  ShoppingBag
} from 'lucide-react';

interface OrderSummary {
  id: string;
  customer_name: string;
  total_products_quantity: number;
  total_products_amount: number;
  shipping_cost: number;
  total_amount: number;
  payment_status: string;
  order_status: string;
  created_at: string;
  items: Array<{
    product_name: string;
    quantity: number;
    image_url?: string;
  }>;
}

const OrderHistoryPage: React.FC = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "My Orders | Strum Music";
    
    if (user) {
      fetchOrders();
    }
  }, [user]);

  const fetchOrders = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          customer_name,
          total_products_quantity,
          total_products_amount,
          shipping_cost,
          total_amount,
          payment_status,
          order_status,
          created_at,
          order_items (
            product_name,
            quantity,
            image_url
          )
        `)
        .eq('customer_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      // Transform the data
      const ordersData: OrderSummary[] = data.map(order => ({
        ...order,
        items: order.order_items || []
      }));

      setOrders(ordersData);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processing':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'confirmed':
        return <CheckCircle className="h-5 w-5 text-blue-600" />;
      case 'shipped':
        return <Truck className="h-5 w-5 text-purple-600" />;
      case 'delivered':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Package className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processing':
        return 'text-yellow-700 bg-yellow-100';
      case 'confirmed':
        return 'text-blue-700 bg-blue-100';
      case 'shipped':
        return 'text-purple-700 bg-purple-100';
      case 'delivered':
        return 'text-green-700 bg-green-100';
      case 'cancelled':
        return 'text-red-700 bg-red-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!user) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Please Sign In</h1>
          <p className="text-gray-600 mb-6">You need to be signed in to view your order history.</p>
          <Link to="/login" className="btn btn-primary px-6 py-2">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container-custom py-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">My Orders</h1>
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-red-700 mr-3" />
            <span className="text-gray-600">Loading your orders...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container-custom py-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">My Orders</h1>
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">Error loading orders: {error}</p>
            <button 
              onClick={fetchOrders}
              className="btn btn-primary px-6 py-2"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container-custom max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-8">My Orders</h1>

          {orders.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No Orders Yet</h2>
              <p className="text-gray-600 mb-6">
                You haven't placed any orders yet. Start shopping to see your orders here.
              </p>
              <Link to="/products" className="btn btn-primary px-6 py-2">
                Start Shopping
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map((order, index) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-sm overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
                      <div className="flex items-center space-x-4 mb-4 lg:mb-0">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Order #{order.id.slice(-8).toUpperCase()}
                          </h3>
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Calendar className="h-4 w-4" />
                            <span>{formatDate(order.created_at)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.order_status)}`}>
                          {getStatusIcon(order.order_status)}
                          <span className="ml-1 capitalize">{order.order_status}</span>
                        </div>
                        <Link
                          to={`/order-confirmation/${order.id}`}
                          className="btn btn-outline py-2 px-4 flex items-center space-x-2"
                        >
                          <Eye className="h-4 w-4" />
                          <span>View Details</span>
                        </Link>
                      </div>
                    </div>

                    {/* Order Items Preview */}
                    <div className="border-t border-gray-200 pt-4">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2">
                          <h4 className="font-medium text-gray-900 mb-3">Items Ordered</h4>
                          <div className="space-y-2">
                            {order.items.slice(0, 3).map((item, itemIndex) => (
                              <div key={itemIndex} className="flex items-center space-x-3">
                                {item.image_url && (
                                  <img 
                                    src={item.image_url} 
                                    alt={item.product_name}
                                    className="w-12 h-12 object-cover rounded-md"
                                  />
                                )}
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">{item.product_name}</p>
                                  <p className="text-xs text-gray-600">Quantity: {item.quantity}</p>
                                </div>
                              </div>
                            ))}
                            {order.items.length > 3 && (
                              <p className="text-sm text-gray-600">
                                +{order.items.length - 3} more item{order.items.length - 3 !== 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="lg:text-right">
                          <h4 className="font-medium text-gray-900 mb-3">Order Summary</h4>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between lg:justify-end lg:space-x-8">
                              <span className="text-gray-600">Items ({order.total_products_quantity}):</span>
                              <span>{formatPrice(order.total_products_amount)}</span>
                            </div>
                            <div className="flex justify-between lg:justify-end lg:space-x-8">
                              <span className="text-gray-600">Shipping:</span>
                              <span>{formatPrice(order.shipping_cost)}</span>
                            </div>
                            <div className="flex justify-between lg:justify-end lg:space-x-8 font-semibold text-lg pt-2 border-t border-gray-200">
                              <span>Total:</span>
                              <span>{formatPrice(order.total_amount)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default OrderHistoryPage;